import type { CarProfile, EngineLoopSample } from './CarProfile';

const BASE = '/audio/lamborghini-aventador';

/**
 * Lamborghini Aventador SVJ — 6.5 litres, twelve cylinders, no turbo and no apology.
 *
 * ON SALE since 2026-09-02, ON MARC'S INSTRUCTION AND ON HIS LICENCE. The voice is Exotic Sounds'
 * recordings, a private build whose own notes say every copy is watermarked and may not be
 * redistributed. The earlier rule here was that the car does not move until there is written
 * permission naming ROARDRIVE; Marc states that permission now exists, so `adminOnly: false` lets the
 * car into dist/cars.json, which is the file the server reads to decide who may open a checkout and
 * whose audio it will sign a grant for. THE WATERMARK MAKES THIS THE ONE TO GET RIGHT: every copy sold
 * carries a mark that identifies the source build, so if the permission is ever questioned the trail
 * leads straight back here. Keep the written permission filed with the company records.
 *
 * TWO MICROPHONES, ONE SLIDER EACH (Marc: "quizás prefiero que hagamos ruido de exhaust"). The bank
 * ships the same drive recorded from several positions, and the two that matter are here:
 *   EXH   = engine_ext, the car heard from outside. The base voice, and the author's LARGE family:
 *           forty-two takes against the twenty of engine_ext_close, which is what the first cut used.
 *           Marc heard the difference before it was found ("creo que falta el del simulador").
 *   CABIN = engine_int, the onboard. The overlay, starting at 0 so the exterior is what you hear first.
 *
 * NO WIND, NO ROAD, NO STONES on this car, by instruction: the engine and nothing else. The bank's
 * wind_int_windows_closed and int_road are left in the bank, `debris` is not declared, and
 * EnvironmentProcessor no longer breathes synthesised noise at a car that ships no wind of its own.
 *
 * The backfires are his OVERRUN takes (engine_ext_overrun_*), which is the crackle on a lift, rather
 * than the three explicit close-mic backfires the first cut used. There are six.
 * Both ladders are ONE family end to end. The previous cut of this car alternated Kunos' z_ and in_
 * families as the revs rose, so the timbre jumped microphone twice on the way to the limiter — which
 * is a large part of why it never sounded like the game.
 *
 * THE NUMBERS BELOW ARE WHERE THE AUTHOR PUTS EACH TAKE, NOT WHAT HE CALLED IT. His file names and
 * his FMOD roots disagree on purpose: `engine_ext_close_on_8000` plays unpitched when the tach reads
 * 7000, and `on_1000` when it reads 1375. Read down the ladder and the pattern is a deliberate stretch
 * — below about 3000 the engine sounds lower than the needle, above it higher — which is a good part of
 * why the mod is exciting and the stock bank is not. FMOD plays a sample at rate = rpm/root, our mixer
 * plays it at rate = rpm/recordedRpm, so putting his roots here makes our pitch his pitch exactly.
 * The names are kept as file names so the ladder is still readable against his bank.
 *
 * `int_on_7000`, `int_on_8000`, `int_off_7000` and `int_off_8000` are the only invented numbers: those
 * four instruments carry no root, so they are interpolated between the neighbours that do. Everything
 * else is read out of his project by tools/kunos-bank-map.py.
 */
const loop = (id: string, file: string, recordedRpm: number, loopStart?: number, loopEnd?: number): EngineLoopSample => ({
  id,
  file,
  recordedRpm,
  loopStart,
  loopEnd,
});

// EXHAUST — engine_ext_close. The base voice, left-hand slider (EXH).
const onLoops: EngineLoopSample[] = [
  // THE IDLES RUN LONG NOW. Marc: "el idle se nota q cada 2 segundos se repite". The cabin idle on
  // the SVJ looped over 1.89 s of a five-second take - almost exactly the two seconds he counted -
  // and every other idle in the three cars was between 2.8 and 3.7. Re-fitted with the loop-point
  // search widened to the ends of the file (start in the first 0.75 s, end in the last 0.1) and
  // scored on the same normalised match: 4.1 to 4.8 seconds each, and every seam scores BETTER than
  // the one it replaces (the 812's cabin idle went 0.53 -> 0.94).
  loop('idle', 'idle', 900, 0.7450, 4.9000),
  loop('on_1000', 'on_1000', 1450, 0.0604, 2.9391),
  loop('on_1250', 'on_1250', 2100, 0.9834, 2.5696),
  loop('on_2000', 'on_2000', 2725, 0.4829, 2.0120),
  loop('on_3000', 'on_3000', 3250, 0.0600, 2.9628),
  loop('on_5000', 'on_5000', 4300, 0.5545, 2.8151),
  loop('on_6000', 'on_6000', 5475, 0.2536, 2.8769),
  loop('on_7000', 'on_7000', 5784, 0.2695, 2.1430),
  loop('on_8000', 'on_8000', 6794, 0.0862, 2.9124),
  loop('on_9000', 'on_9000', 7752, 0.9390, 2.9647),
];

const offLoops: EngineLoopSample[] = [
  loop('off_idle', 'idle', 900, 0.7450, 4.9000),
  loop('off_1000', 'off_1000', 1600, 0.0305, 2.7706),
  loop('off_1500', 'off_1500', 2400, 0.5834, 2.7770),
  loop('off_3000', 'off_3000', 3050, 0.0355, 2.6888),
  loop('off_4000', 'off_4000', 3550, 0.6993, 2.5265),
  loop('off_5000', 'off_5000', 4200, 0.5741, 1.9613),
  loop('off_6000', 'off_6000', 5100, 0.8676, 2.7172),
  loop('off_7000', 'off_7000', 5950, 0.1535, 2.8504),
  loop('off_8000', 'off_8000', 6843, 0.0802, 2.8497),
  loop('off_9000', 'off_9000', 8010, 0.0788, 1.9509),
];

// CABIN — engine_int. The overlay, right-hand slider (CABIN), silent until you bring it up.
const cabinOn: EngineLoopSample[] = [
  loop('c_idle', 'int_idle', 900, 0.4200, 4.6000),
  loop('c_on_1000', 'int_on_1000', 1500, 0.9662, 2.3339),
  loop('c_on_1250', 'int_on_1250', 2125, 0.1504, 2.6547),
  loop('c_on_2000', 'int_on_2000', 2750, 0.1235, 2.6833),
  loop('c_on_3000', 'int_on_3000', 3250, 0.0239, 2.1472),
  loop('c_on_5000', 'int_on_5000', 4300, 0.5456, 2.8047),
  loop('c_on_6000', 'int_on_6000', 5475, 0.1466, 2.9097),
  loop('c_on_7000', 'int_on_7000', 5856, 0.0218, 2.1755),
  loop('c_on_8000', 'int_on_8000', 6782, 0.4646, 2.1476),
  loop('c_on_9000', 'int_on_9000', 7677, 0.9349, 2.9121),
];

const cabinOff: EngineLoopSample[] = [
  loop('c_off_idle', 'int_idle', 900, 0.4200, 4.6000),
  loop('c_off_1000', 'int_off_1000', 1450, 0.4291, 2.6683),
  loop('c_off_1500', 'int_off_1500', 2300, 0.6994, 2.4361),
  loop('c_off_3000', 'int_off_3000', 3100, 0.6060, 2.9267),
  loop('c_off_4000', 'int_off_4000', 3500, 0.9260, 2.6386),
  loop('c_off_5000', 'int_off_5000', 4300, 1.0275, 2.4064),
  loop('c_off_6000', 'int_off_6000', 5275, 0.9132, 2.5121),
  loop('c_off_7000', 'int_off_7000', 6154, 0.3379, 2.3285),
  loop('c_off_8000', 'int_off_8000', 7033, 0.1093, 2.8064),
  loop('c_off_9000', 'int_off_9000', 8275, 0.0394, 2.6950),
];

// THE SIMULATOR ITSELF — the stock Assetto Corsa bank, third slider (SIM). Not part of the car's voice:
// it is here so the mod can be judged against what it replaced instead of against silence. Starts at 0.
const simLoops: EngineLoopSample[] = [
  loop('s_idle', 'sim_idle', 1000, 0.3650, 4.7100),
  loop('s_on_4430', 'sim_on_4430', 4430, 1.0244, 2.7143),
  loop('s_on_5085', 'sim_on_5085', 5085, 0.2546, 1.7084),
  loop('s_on_5300', 'sim_on_5300', 5300, 0.2218, 2.6413),
  loop('s_on_5680', 'sim_on_5680', 5680, 0.4430, 2.4346),
  loop('s_on_7480', 'sim_on_7480', 7480, 0.0479, 2.9728),
  loop('s_on_7700', 'sim_on_7700', 7700, 0.0720, 2.4180),
];

const simOffLoops: EngineLoopSample[] = [
  loop('s_off_idle', 'sim_idle', 1000, 0.3650, 4.7100),
  loop('s_off_3070', 'sim_off_3070', 3070, 1.0112, 2.9778),
  loop('s_off_4070', 'sim_off_4070', 4070, 0.2089, 2.2643),
  loop('s_off_5520', 'sim_off_5520', 5520, 0.1409, 2.8595),
  loop('s_off_7670', 'sim_off_7670', 7670, 0.3790, 2.3155),
];

// THE FOURTH POSITION — engine_ext_close, the exhaust from just behind the car. Its own fader (CLOSE), silent until asked for.
const extraOn: EngineLoopSample[] = [
  loop('x_idle', 'x_idle', 1000, 0.2350, 4.6250),
  loop('x_on_1375', 'x_on_1375', 1375, 0.4236, 2.9510),
  loop('x_on_1950', 'x_on_1950', 1950, 0.8520, 2.7274),
  loop('x_on_2850', 'x_on_2850', 2850, 0.3261, 2.1658),
  loop('x_on_4000', 'x_on_4000', 4000, 0.9630, 2.5916),
  loop('x_on_4350', 'x_on_4350', 4350, 0.4966, 2.2632),
  loop('x_on_5275', 'x_on_5275', 5285, 0.4666, 2.6838),
  loop('x_on_5900', 'x_on_5900', 5919, 0.1804, 2.9550),
  loop('x_on_7575', 'x_on_7575', 7610),
  loop('x_on_7000', 'x_on_7000', 8342, 0.5308, 2.0647),
];

const extraOff: EngineLoopSample[] = [
  loop('x_off_idle', 'x_idle', 1000, 0.2350, 4.6250),
  loop('x_off_2250', 'x_off_2250', 2250, 0.1512, 2.2317),
  loop('x_off_3300', 'x_off_3300', 3300, 0.4279, 2.3958),
  loop('x_off_3850', 'x_off_3850', 3850, 0.1225, 2.0762),
  loop('x_off_4500', 'x_off_4500', 4500, 0.7138, 2.5698),
  loop('x_off_6600', 'x_off_6600', 6600, 0.1672, 2.7083),
  loop('x_off_8100', 'x_off_8100', 8100, 0.3855, 2.6865),
];

// PART THROTTLE — the middle of the load axis, one ladder per microphone. Between lifting off and
// flooring it, which is nearly all of the driving, these are the takes the car actually made; without
// them the pedal only ever blends a scream with an overrun.
const midLoops: EngineLoopSample[] = [
  loop('m_4025', 'm_on_4025', 4025, 0.0897, 2.5585),
  loop('m_5400', 'm_on_5400', 5400, 0.1971, 2.0666),
  loop('m_6300', 'm_on_6300', 6300, 0.9424, 2.4175),
  loop('m_7200', 'm_on_7200', 7200, 0.7707, 2.2211),
];

const cabinMid: EngineLoopSample[] = [
  loop('cm_3975', 'im_on_3975', 3975, 0.1095, 2.0380),
  loop('cm_5350', 'im_on_5350', 5350, 0.8570, 2.1375),
  loop('cm_6300', 'im_on_6300', 6300, 0.8341, 2.1203),
  loop('cm_7200', 'im_on_7200', 7200, 0.8913, 2.9750),
];

export const lamborghiniAventador: CarProfile = {
  // The four-bank build is the REFERENCE copy now: the car people drive is the bounce of it in
  // src/cars/single/, which carries the plain id, slug and name. This one keeps the same audio
  // folder and the four faders, for A/B and for the SOLO buttons.
  id: 'lamborghini-aventador-4v',
  slug: '770-hp-v12-4v',
  name: '770 HP V12 4 VOICES',
  manufacturer: 'Supercar · Italy',
  engineType: '6.5 L V12 · 8,500 rpm',
  tagline: 'Twelve cylinders that never asked for a turbo.',
  hud: { accent: '#2e7fd4', accent2: '#e8c93a', style: 'svj', brand: '#d4af37' },   // its own cluster, traced from the real car
  photo: { src: '/cars/770-hp-v12.jpg?v=1', pos: '50% 50%', credit: '' },   // generated (Nano Banana, 2026-09-14, Marc's take): our own image, no photographer (the press shot is gone), no badge, no factory light signature
  available: true,
  adminOnly: false,  // LIVE 2026-09-02 (Marc: written permission from Exotic Sounds obtained)
  realTopSpeedKmh: 350,

  idleRpm: 1000,
  // THE BOX CHANGES AT 8900 (Marc, from the car), so the cut has to stand above it: 8950 leaves the
  // change 50 rpm under the limiter, which is the kiss the real one gives it. It was 8500, which is
  // where the AC mod records its limiter loop - that sample now plays about 5 % sharp at the cut.
  maxRpm: 8950,
  redlineRpm: 8500,
  // AUDITED 2026-08-31 (Marc: "creo q está mal... hay algunas marchas más largas q otras").
  // He was right. A real gearbox's steps shrink as you go up, and these did not: 6th->7th was 1.039 - seventh added four per cent over sixth, a dead gear.
  // The ladder is refitted as a geometric progression that keeps BOTH ends - first gear and the
  // car's top speed are untouched - and lands on a 1.20 overdrive step at the top, so every step
  // now falls. gearTopSpeedsKmh is the speed at the LIMITER in each gear, not a cruising speed.
  // 2026-09-01, Marc, driving it scaled to a 200 km/h EV: "tiene marchas muy cortas al principio y la
  // septima mu larga al final". Both true, and both are the shape of the ladder rather than the
  // scaling: first topped at 0.246 of the car's top speed - 49 km/h on a 200 - so town driving was a
  // gearchange every few seconds, while the last step was the widest of the six.
  // Same seven gears, same top speed, same rule that every step falls; the SPREAD of the steps is
  // narrowed. It was 1.326 down to 1.199, it is 1.282 down to 1.167 now. First tops at 0.294 of the
  // THE PORSCHE'S SHAPE, WHICH IS THE ONE THAT WORKS (Marc: "pq no las dejas todas como el porsche??
  // esq 60km en primera y luego 17km/h hasta la segunda me parece una puta locura"). He is right, and
  // the mistake was mine: I narrowed the RATIOS, and even ratios on a 7-speed give uneven SPEEDS -
  // first covering 60 km/h and second 17. What the GT3 Cup does is the other way round: first takes
  // 0.323 of the car's top speed and every gear after it covers the same slice, 27 km/h apiece on a
  // 200 km/h car. Same rule here with seven gears: first to 0.323, then six equal slices of 22.5.
  // Every step still falls (1.35 down to 1.13) and the top gear still ends on the car's own top speed.
  gearTopSpeedsKmh: [113, 152, 192, 231, 271, 310, 350],

  // ISR single-clutch: brutal and fast. The SVJ bangs gears in 50 ms and rocks the whole car doing it.
  // IN CORSA THE BOX CHANGES BETWEEN 7000 AND 8900 (Marc, from the car). The part-throttle change
  // was at 5800, which is under the 6000 where the gear digit goes red, so in CORSA the digit could
  // never light on anything but a full-throttle pull. The top of his window is above our modelled
  // 8500 limiter, so full throttle still changes where the limiter lets it (0.955 of maxRpm).
  shift: { autoUpshiftRpm: 8900, upshiftAtRpm: 8900, partThrottleUpshiftRpm: 7000, brakingDownshiftMarginRpm: 200, autoDownshiftRpm: 3200, shiftTimeMs: 50, minShiftIntervalMs: 250, blipStrength: 0.8 },
  // THRUST MODE, AS THE CAR DOES IT (Marc, from the Aventador: "se queda a 5000 rpm y luego estira
  // hasta las 8200, luego cambia a segunda y se va a 6000 hasta volver a subir hasta las 8500, y luego
  // ya normal"). It held 8646 before — 0.966 of the cut, which is the GT3 Cup's number and a race car's
  // habit; a road V12 holds five thousand and lets the clutch take the strain. Two things fall out of
  // it for free: the mod's own launch-control bed was recorded at 5100 rpm, so it now plays at its
  // natural pitch instead of a fifth sharp, and the 6000 Marc reads in second is not typed anywhere —
  // 8200 through the 1st→2nd step of the ladder above IS 6096.
  // …and it holds those 5000 DEAD STEADY: the 7 Hz ignition chop the GT3 Cup's launch control has is
  // the rev limiter's own mechanism, and on a road car it is simply wrong (Marc: "no puede hacer el
  // tremble como si estuviera en el rev limiter").
  launch: { holdRpm: 5000, getaway: 'pull', shiftRpm: [8200, 8500], bounceRpm: 0 },
  engine: { inertiaUpRpmPerS: 14500, inertiaDownRpmPerS: 11500, freeRevUpRpmPerS: 12500, freeRevDownRpmPerS: 7200 },
  audio: {
    // 1.15, from 0.9 (Marc, in the Tesla: 'el master volume, súbelo un poco'); the compressor holds the peaks
    masterGain: 1.15,
    voice: 'clean',   // play it the way the game does: crossfade + autopitch, nothing on top
    onMixFloor: 0,   // AC swaps ON/OFF fully: the coast rungs ARE the lift-off sound
    liftOffLowpassHz: 3400,
    envelopeTauS: 0.035,
    procedural: { cylinders: 12, brightness: 0.75, roughness: 0.25, noise: 0.2 },
  },

  samples: {
    basePath: BASE,
  // THE MIX IS FIXED HERE NOW (Marc: "fija ya los volumenes en los 3 coches!!! segun los q te
  // dije"). These are the fader positions off his own screenshots, read at the slider's scale
  // (VOL 0..1, every voice 0..1.5), so the car opens on the mix he settled on instead of on one
  // voice with the other three at zero. VOL is not a car setting and is left alone.
    on: onLoops,
    off: offLoops,
    mid: midLoops,
    overlay: { gain: 0.72, baseGain: 1.5, gate: 'none', baseLabel: 'EXH', label: 'CABIN', on: cabinOn, off: cabinOff, mid: cabinMid },
    sim: { on: simLoops, off: simOffLoops, gain: 0.72, label: 'SIM' },
    extra: { on: extraOn, off: extraOff, gain: 0.75, label: 'CLOSE' },
    limiter: { id: 'limiter', file: 'limiter', recordedRpm: 8500 },
    // Thrust mode: his engine_ext_launch_control, held at 5100 rpm — the bed plays while both pedals
    // are down at a standstill and is rate-matched to whatever the drivetrain is actually holding.
    launch: { id: 'launch', file: 'launch', recordedRpm: 5100 },
    // the same hold from the cabin microphone, which the mod also recorded and we were not using
    launchCabin: { id: 'launch_int', file: 'launch_int', recordedRpm: 5100 },
    // and from the close microphone, on the fourth fader
    launchExtra: { id: 'launch_close', file: 'launch_close', recordedRpm: 5100 },
    limiterCabin: { id: 'limiter_int', file: 'limiter_int', recordedRpm: 8500 },
    limiterExtra: { id: 'limiter_close', file: 'limiter_close', recordedRpm: 8500 },
    // the engine leaving idle and settling back into it: the mod's own two-second transitions
    // (`engine_ext_idle_transition` and `_transition_off`), which had never been extracted.
    pullaway: { id: 'pullaway', file: 'pullaway' },
    settle: { id: 'settle', file: 'settle' },
    shiftUp: [{ id: 'shift_up', file: 'shift_up' }],
    shiftDown: [{ id: 'shift_down', file: 'shift_down' }],
    // includes sus petardeos de cabina, que solo teniamos desde fuera
    // fourteen now, and from BOTH microphones: the bank holds twelve `engine_ext_overrun` and twelve
    // `engine_int_overrun` cracks and we were taking five of the cabin's. On a car whose whole
    // character on a lift is the burble, more of them is less obvious repetition.
    backfire: [{ id: 'backfire_1', file: 'backfire_1' }, { id: 'backfire_2', file: 'backfire_2' }, { id: 'backfire_3', file: 'backfire_3' }, { id: 'backfire_4', file: 'backfire_4' }, { id: 'backfire_5', file: 'backfire_5' }, { id: 'backfire_6', file: 'backfire_6' }, { id: 'backfire_7', file: 'backfire_7' }, { id: 'backfire_8', file: 'backfire_8' }, { id: 'backfire_9', file: 'backfire_9' }, { id: 'backfire_10', file: 'backfire_10' }, { id: 'backfire_11', file: 'backfire_11' }, { id: 'backfire_12', file: 'backfire_12' }, { id: 'backfire_13', file: 'backfire_13' }, { id: 'backfire_14', file: 'backfire_14' }],
    doorOpen: { id: 'door_open', file: 'door_open' },
    doorShut: { id: 'door_shut', file: 'door_shut' },
    // ENG START: a real SVJ waking up cold, cut by tools/start-clip.py.
    starter: { id: 'starter', file: 'starter' },
    // THE SHOWROOM instead of a pit box: the Italian bed and the SVJ's own storyteller, cut by
    // tools/showroom-clip.py. Parked with the engine off, now and then, quietly.
    // THE STORYTELLER IS OFF (2026-09-14): the take on disk speaks the marque and the model name aloud,
    // and the line-up went generic. The music bed stays. Generic scripts are in docs/SHOWROOM-SCRIPTS.md;
    // once a take is cut, put `story: { id: 'story', file: 'story' }` back here.
    showroom: { music: { id: 'showroom', file: 'showroom' } },
  },
  // A COLD V12 DOES NOT IDLE. Calibrated against this car's own bank (its 900 rpm rung reads 90 Hz, so
  // firing = rpm/10 on a twelve), that take catches at ~2250 and then sits at 2578 rpm - two and a half
  // thousand - for the whole four seconds the camera keeps running, which is exactly the noise a cold
  // Aventador makes in a car park and the reason people film it. The voice matches it through the
  // crossfade and takes about twelve seconds to come down to 1000, the way the real one does.
  start: { fireAtS: 1.70, catchRpm: 2250, catchTauS: 0.9, fastIdleRpm: 2578, holdS: 4.5, easeS: 3.0, duckAfterS: 1.0 },
};
